<div class="trace-dialog">
    <!-- Header -->
    <div class="dialog-header">
        <div class="header-main">
            <div class="title-row">
                <h2 mat-dialog-title class="title" [title]="trace?.Upload?.FileName || fileName || runId">
                    {{ trace?.Upload?.FileName || fileName || 'Upload trace' }}
                </h2>
                <span class="df-badge" *ngIf="trace" [ngClass]="'tone-' + stageMeta().tone">
                    <mat-icon aria-hidden="true" [class.df-spin]="stageMeta().spin">{{ stageMeta().icon }}</mat-icon>
                    {{ stageMeta().label }}
                </span>
                <span class="df-badge pending-chip" *ngIf="pendingControl as pending"
                    [ngClass]="'tone-' + pending.tone" [matTooltip]="pending.tooltip">
                    <mat-icon aria-hidden="true">pending</mat-icon>
                    {{ pending.label }}
                </span>
                <span class="live-chip" *ngIf="running" matTooltip="Refreshing every 3 seconds">
                    <span class="live-dot" aria-hidden="true"></span>Live
                </span>
            </div>
            <div class="sub-row">
                <span class="display-name" *ngIf="trace?.Upload?.DisplayName && trace?.Upload?.DisplayName !== trace?.Upload?.FileName">
                    {{ trace?.Upload?.DisplayName }}
                </span>
                <span class="run-id">
                    Run <code>{{ runId }}</code>
                    <button mat-icon-button type="button" class="tiny-btn" (click)="copy(runId, 'Run id')"
                        aria-label="Copy run id" matTooltip="Copy run id">
                        <mat-icon>content_copy</mat-icon>
                    </button>
                </span>
            </div>
        </div>
        <div class="header-actions" *ngIf="trace?.Upload?.CanPause || trace?.Upload?.CanResume || trace?.Upload?.CanCancel">
            <button mat-stroked-button type="button" class="control-btn pause-btn" *ngIf="trace?.Upload?.CanPause"
                matTooltip="Pause &mdash; stops now, keeps progress; Start continues it later"
                [disabled]="runControl.isBusy(runId)" (click)="controlRun('pause')">
                <mat-icon>pause_circle</mat-icon>
                Pause
            </button>
            <button mat-stroked-button type="button" class="control-btn" *ngIf="trace?.Upload?.CanResume"
                matTooltip="Start &mdash; continue from where it stopped"
                [disabled]="runControl.isBusy(runId)" (click)="controlRun('resume')">
                <mat-icon>play_circle</mat-icon>
                Start
            </button>
            <button mat-stroked-button type="button" class="control-btn cancel-btn" *ngIf="trace?.Upload?.CanCancel"
                matTooltip="Stop &mdash; cancel this import for good"
                [disabled]="runControl.isBusy(runId)" (click)="controlRun('cancel')">
                <mat-icon>stop_circle</mat-icon>
                Stop
            </button>
        </div>
        <button mat-icon-button type="button" (click)="close()" class="close-btn" aria-label="Close trace">
            <mat-icon>close</mat-icon>
        </button>
    </div>

    <div class="dialog-body">
        <!-- First-load skeleton -->
        <div class="trace-skeleton" *ngIf="loading" aria-busy="true" aria-label="Loading trace">
            <div class="skeleton-grid">
                <div *ngFor="let r of skeletonRows" class="skeleton-fact">
                    <span class="df-skeleton" style="width: 40%"></span>
                    <span class="df-skeleton" style="width: 75%"></span>
                </div>
            </div>
            <span class="df-skeleton" style="width: 100%; height: 48px"></span>
        </div>

        <!-- Load error -->
        <div class="state-block error-state" *ngIf="!loading && loadError" role="alert">
            <mat-icon aria-hidden="true">error_outline</mat-icon>
            <p>{{ loadError }}</p>
            <button mat-stroked-button color="primary" type="button" (click)="retry()">Retry</button>
        </div>

        <div class="poll-error" *ngIf="pollError && trace" role="alert">
            <mat-icon aria-hidden="true">sync_problem</mat-icon>
            <span>{{ pollError }} Showing the last data received.</span>
            <button mat-button type="button" (click)="retry()">Retry</button>
        </div>

        <ng-container *ngIf="!loading && trace as t">
            <mat-tab-group class="trace-tabs" animationDuration="200ms" mat-stretch-tabs="false"
                [selectedIndex]="selectedTab" (selectedIndexChange)="onTabChange($event)">

                <!-- ===================== Overview ===================== -->
                <mat-tab label="Overview">
                    <div class="tab-pane">
                        <div class="alert-box" *ngIf="alertText" [ngClass]="'tone-' + alertTone" role="status">
                            <mat-icon aria-hidden="true">{{ alertIcon }}</mat-icon>
                            <div>
                                <strong>{{ t.Upload.FailureReason ? 'Failure reason' : 'Status' }}</strong>
                                <p>{{ alertText }}</p>
                            </div>
                        </div>

                        <dl class="facts-grid">
                            <div class="fact">
                                <dt>Run id</dt>
                                <dd class="mono">{{ t.Upload.RunId || runId }}</dd>
                            </div>
                            <div class="fact">
                                <dt>Stage / Status / Outcome</dt>
                                <dd>{{ stageMeta().label }} / {{ t.Upload.Status || '-' }} / {{ t.Upload.Outcome || '-' }}</dd>
                            </div>
                            <div class="fact">
                                <dt>Duration</dt>
                                <dd [class.live]="t.Upload.Stage === 'Processing'">{{ runDuration() }}</dd>
                            </div>
                            <div class="fact">
                                <dt>Attempts</dt>
                                <dd><span [class.retried]="t.Upload.Attempts > 1">{{ t.Upload.Attempts }}</span></dd>
                            </div>
                            <div class="fact">
                                <dt>Uploaded</dt>
                                <dd>{{ t.Upload.UploadedAt ? (t.Upload.UploadedAt | date:'MMM d, y, h:mm:ss a') : '-' }}</dd>
                            </div>
                            <div class="fact">
                                <dt>Queued</dt>
                                <dd>{{ t.Upload.QueuedAt ? (t.Upload.QueuedAt | date:'MMM d, y, h:mm:ss a') : '-' }}</dd>
                            </div>
                            <div class="fact">
                                <dt>Started</dt>
                                <dd>{{ t.Upload.StartedAt ? (t.Upload.StartedAt | date:'MMM d, y, h:mm:ss a') : '-' }}</dd>
                            </div>
                            <div class="fact">
                                <dt>Completed</dt>
                                <dd>{{ t.Upload.CompletedAt ? (t.Upload.CompletedAt | date:'MMM d, y, h:mm:ss a') : '-' }}</dd>
                            </div>
                            <div class="fact">
                                <dt>Records ok / failed</dt>
                                <dd>
                                    <span class="ok">{{ (t.Upload.RecordsSucceeded ?? 0) | number }}</span>
                                    /
                                    <span [class.bad]="(t.Upload.RecordsFailed ?? 0) > 0">{{ (t.Upload.RecordsFailed ?? 0) | number }}</span>
                                </dd>
                            </div>
                            <div class="fact">
                                <dt>Worker instance</dt>
                                <dd class="mono">{{ t.Upload.WorkerInstance || '-' }}</dd>
                            </div>
                            <div class="fact">
                                <dt>IA (JAI) enabled</dt>
                                <dd>{{ t.Upload.IsJaiEnabled === null ? 'Unknown' : (t.Upload.IsJaiEnabled ? 'Yes' : 'No') }}</dd>
                            </div>
                            <div class="fact">
                                <dt>In upload folder</dt>
                                <dd>{{ t.Upload.InUploadFolder ? 'Yes' : 'No' }}</dd>
                            </div>
                            <div class="fact fact-wide" *ngIf="t.Upload.BlobUrl">
                                <dt>Blob URL</dt>
                                <dd class="mono with-copy">
                                    <span class="break">{{ t.Upload.BlobUrl }}</span>
                                    <button mat-icon-button type="button" class="tiny-btn" (click)="copy(t.Upload.BlobUrl, 'Blob URL')"
                                        aria-label="Copy blob URL" matTooltip="Copy blob URL">
                                        <mat-icon>content_copy</mat-icon>
                                    </button>
                                </dd>
                            </div>
                        </dl>

                        <h3 class="section-title">Pipeline</h3>
                        <ol class="pipeline" *ngIf="entities.length; else noEntities">
                            <li *ngFor="let e of entities; trackBy: trackByEntity" class="pipeline-step"
                                [ngClass]="'tone-' + entityMeta(e).tone">
                                <span class="step-icon" aria-hidden="true">
                                    <mat-icon [class.df-spin]="entityMeta(e).spin">{{ entityMeta(e).icon }}</mat-icon>
                                </span>
                                <div class="step-body">
                                    <span class="step-name">{{ e.Entity }}</span>
                                    <span class="step-status">{{ entityMeta(e).label }}</span>
                                    <span class="step-counts">
                                        {{ e.Succeeded | number }} ok<ng-container *ngIf="e.Failed > 0"> &middot; <span class="bad">{{ e.Failed | number }} failed</span></ng-container>
                                    </span>
                                </div>
                            </li>
                        </ol>
                        <ng-template #noEntities>
                            <p class="empty-inline">No entities have been recorded for this run yet.</p>
                        </ng-template>
                    </div>
                </mat-tab>

                <!-- ===================== Entities ===================== -->
                <mat-tab label="Entities">
                    <div class="tab-pane">
                        <div class="table-scroll" *ngIf="entities.length; else noEntitiesTable">
                            <table class="df-table">
                                <thead>
                                    <tr>
                                        <th scope="col">Entity</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Validation</th>
                                        <th scope="col" class="num">Total</th>
                                        <th scope="col" class="num">Succeeded</th>
                                        <th scope="col" class="num">Failed</th>
                                        <th scope="col" class="num">Skipped</th>
                                        <th scope="col">Duration</th>
                                        <th scope="col" class="progress-col">Progress</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr *ngFor="let e of entities; trackBy: trackByEntity">
                                        <td class="strong cap">{{ e.Entity }}</td>
                                        <td>
                                            <span class="df-badge" [ngClass]="'tone-' + entityMeta(e).tone">
                                                <mat-icon aria-hidden="true" [class.df-spin]="entityMeta(e).spin">{{ entityMeta(e).icon }}</mat-icon>
                                                {{ entityMeta(e).label }}
                                            </span>
                                        </td>
                                        <td>
                                            <span *ngIf="e.ValidationStatus; else dash" class="df-badge"
                                                [ngClass]="'tone-' + statusMeta(e.ValidationStatus).tone">
                                                <mat-icon aria-hidden="true">{{ statusMeta(e.ValidationStatus).icon }}</mat-icon>
                                                {{ e.ValidationStatus }}
                                            </span>
                                        </td>
                                        <td class="num">{{ e.Total !== null ? (e.Total | number) : '-' }}</td>
                                        <td class="num ok">{{ e.Succeeded | number }}</td>
                                        <td class="num">
                                            <button *ngIf="e.Failed > 0; else zeroFailed" type="button" class="link-btn bad"
                                                (click)="showEntityErrors(e.Entity)"
                                                [attr.aria-label]="'Show ' + e.Failed + ' errors for ' + e.Entity">
                                                {{ e.Failed | number }}
                                            </button>
                                            <ng-template #zeroFailed>0</ng-template>
                                        </td>
                                        <td class="num">{{ e.Skipped !== null ? (e.Skipped | number) : '-' }}</td>
                                        <td class="nowrap">{{ entityDuration(e) }}</td>
                                        <td class="progress-col">
                                            <mat-progress-bar
                                                [mode]="entityProgress(e) === null ? 'indeterminate' : 'determinate'"
                                                [value]="entityProgress(e) ?? 0"
                                                [ngClass]="'bar-' + entityMeta(e).tone"
                                                [attr.aria-label]="e.Entity + ' progress'"></mat-progress-bar>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <ng-template #noEntitiesTable>
                            <p class="empty-inline">No entities have been recorded for this run yet.</p>
                        </ng-template>
                    </div>
                </mat-tab>

                <!-- ===================== Errors ===================== -->
                <mat-tab>
                    <ng-template mat-tab-label>
                        Errors
                        <span class="tab-count" [class.has-errors]="totalIssues > 0">{{ totalIssues | number }}</span>
                    </ng-template>
                    <div class="tab-pane">
                        <ng-container *ngIf="totalIssues > 0 || issueEntity || issueKind; else noIssues">
                            <div class="issues-toolbar">
                                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="filter-field">
                                    <mat-label>Entity</mat-label>
                                    <mat-select [(value)]="issueEntity" (selectionChange)="onIssueFilterChange()">
                                        <mat-option value="">All entities</mat-option>
                                        <mat-option *ngFor="let o of issueEntities; trackBy: trackByName" [value]="o.name">
                                            {{ o.name }} ({{ o.count | number }})
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="filter-field">
                                    <mat-label>Kind</mat-label>
                                    <mat-select [(value)]="issueKind" (selectionChange)="onIssueFilterChange()">
                                        <mat-option value="">All kinds</mat-option>
                                        <mat-option *ngFor="let o of issueKinds; trackBy: trackByName" [value]="o.name">
                                            {{ o.name }} ({{ o.count | number }})
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                                <span class="spacer"></span>
                                <button mat-stroked-button type="button" (click)="copyIssuesOnPage()"
                                    [disabled]="!issuePage?.Items?.length">
                                    <mat-icon>content_copy</mat-icon>
                                    Copy all on page
                                </button>
                            </div>

                            <mat-progress-bar mode="indeterminate" *ngIf="issuesLoading" class="issues-loading"
                                aria-label="Loading errors"></mat-progress-bar>

                            <div class="poll-error" *ngIf="issuesError" role="alert">
                                <mat-icon aria-hidden="true">error_outline</mat-icon>
                                <span>{{ issuesError }}</span>
                                <button mat-button type="button" (click)="loadIssues()">Retry</button>
                            </div>

                            <div class="issue-list" *ngIf="issuePage" role="list">
                                <div class="issue-head" aria-hidden="true">
                                    <span></span>
                                    <span>Entity</span>
                                    <span>Kind</span>
                                    <span>Client key</span>
                                    <span>Message</span>
                                    <span>Occurred</span>
                                </div>
                                <div class="issue" role="listitem" *ngFor="let i of issuePage.Items; trackBy: trackByIssue"
                                    [class.expanded]="expandedIssues.has(i.IssueId)">
                                    <button type="button" class="issue-row" (click)="toggleIssue(i)"
                                        [attr.aria-expanded]="expandedIssues.has(i.IssueId)"
                                        [attr.aria-controls]="'issue-' + i.IssueId">
                                        <mat-icon class="chevron" aria-hidden="true">chevron_right</mat-icon>
                                        <span class="cap strong">{{ i.Entity }}</span>
                                        <span><span class="kind-chip">{{ i.Kind }}</span></span>
                                        <span class="mono ellipsis" [title]="i.ClientKey || ''">{{ i.ClientKey || '-' }}</span>
                                        <span class="message">{{ i.Message }}</span>
                                        <span class="nowrap muted">{{ i.OccurredAt | date:'MMM d, h:mm:ss a' }}</span>
                                    </button>
                                    <div class="issue-detail" *ngIf="expandedIssues.has(i.IssueId)" [id]="'issue-' + i.IssueId">
                                        <div class="detail-head">
                                            <span>Record</span>
                                            <button mat-stroked-button type="button" class="copy-small" (click)="copyIssueRecord(i)">
                                                <mat-icon>content_copy</mat-icon>
                                                Copy
                                            </button>
                                        </div>
                                        <pre class="code-block" *ngIf="i.Record; else noRecord" tabindex="0">{{ pretty(i.Record) }}</pre>
                                        <ng-template #noRecord>
                                            <p class="empty-inline">No record payload was captured for this issue.</p>
                                        </ng-template>
                                    </div>
                                </div>
                                <p class="empty-inline" *ngIf="!issuePage.Items.length && !issuesLoading">
                                    No errors match the selected filters.
                                </p>
                            </div>

                            <mat-paginator [length]="issuePage?.TotalCount ?? 0" [pageIndex]="issuePageIndex"
                                [pageSize]="issuePageSize" [pageSizeOptions]="pageSizeOptions" (page)="onIssuePage($event)"
                                aria-label="Select page of errors"></mat-paginator>
                        </ng-container>
                        <ng-template #noIssues>
                            <div class="state-block success-state">
                                <mat-icon aria-hidden="true">task_alt</mat-icon>
                                <p>No record or validation errors were recorded for this run.</p>
                            </div>
                        </ng-template>
                    </div>
                </mat-tab>

                <!-- ===================== Timeline ===================== -->
                <mat-tab label="Timeline">
                    <div class="tab-pane">
                        <div class="timeline-toolbar">
                            <mat-slide-toggle [(ngModel)]="problemsOnly" color="primary">
                                Warnings and errors only
                                <ng-container *ngIf="problemEventCount > 0">({{ problemEventCount }})</ng-container>
                            </mat-slide-toggle>
                            <span class="muted">{{ visibleEvents.length }} of {{ t.Events.length }} events</span>
                        </div>

                        <ol class="timeline" *ngIf="visibleEvents.length; else noEvents">
                            <li *ngFor="let ev of visibleEvents; trackBy: trackByEvent" class="tl-item"
                                [ngClass]="'tone-' + eventMeta(ev).tone">
                                <span class="tl-marker" aria-hidden="true">
                                    <mat-icon>{{ eventMeta(ev).icon }}</mat-icon>
                                </span>
                                <div class="tl-content">
                                    <div class="tl-head">
                                        <time class="tl-time" [attr.datetime]="ev.OccurredAt">{{ ev.OccurredAt | date:'MMM d, h:mm:ss.SSS a' }}</time>
                                        <span class="tl-type">{{ ev.EventType }}</span>
                                        <span class="df-badge" [ngClass]="'tone-' + severityMeta(ev).tone">{{ severityMeta(ev).label }}</span>
                                        <span class="tl-meta" *ngIf="ev.Attempt !== null">Attempt {{ ev.Attempt }}</span>
                                        <span class="tl-meta mono" *ngIf="ev.Instance">{{ ev.Instance }}</span>
                                    </div>
                                    <p class="tl-message" *ngIf="ev.Message">{{ ev.Message }}</p>
                                    <ng-container *ngIf="ev.Data">
                                        <button type="button" class="link-btn" (click)="toggleEvent(ev)"
                                            [attr.aria-expanded]="expandedEvents.has(ev.EventId)"
                                            [attr.aria-controls]="'event-' + ev.EventId">
                                            <mat-icon aria-hidden="true">{{ expandedEvents.has(ev.EventId) ? 'expand_less' : 'expand_more' }}</mat-icon>
                                            {{ expandedEvents.has(ev.EventId) ? 'Hide data' : 'Show data' }}
                                        </button>
                                        <div *ngIf="expandedEvents.has(ev.EventId)" class="tl-data" [id]="'event-' + ev.EventId">
                                            <button mat-icon-button type="button" class="tiny-btn copy-float"
                                                (click)="copy(pretty(ev.Data), 'Event data')" aria-label="Copy event data"
                                                matTooltip="Copy event data">
                                                <mat-icon>content_copy</mat-icon>
                                            </button>
                                            <pre class="code-block" tabindex="0">{{ pretty(ev.Data) }}</pre>
                                        </div>
                                    </ng-container>
                                </div>
                            </li>
                        </ol>
                        <ng-template #noEvents>
                            <p class="empty-inline">{{ problemsOnly ? 'No warnings or errors were logged for this run.' : 'No events have been recorded for this run yet.' }}</p>
                        </ng-template>
                    </div>
                </mat-tab>

                <!-- ===================== IA steps ===================== -->
                <mat-tab *ngIf="t.IaSteps.length" label="IA steps">
                    <div class="tab-pane">
                        <div class="table-scroll">
                            <table class="df-table">
                                <thead>
                                    <tr>
                                        <th scope="col">Kind</th>
                                        <th scope="col">Status</th>
                                        <th scope="col" class="num">Offers</th>
                                        <th scope="col">Duration</th>
                                        <th scope="col">Created</th>
                                        <th scope="col">Error</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr *ngFor="let s of t.IaSteps">
                                        <td class="strong">{{ s.Kind }}</td>
                                        <td>
                                            <span class="df-badge" [ngClass]="'tone-' + statusMeta(s.Status).tone">
                                                <mat-icon aria-hidden="true">{{ statusMeta(s.Status).icon }}</mat-icon>
                                                {{ s.Status }}
                                            </span>
                                        </td>
                                        <td class="num">{{ s.TotalOffers | number }}</td>
                                        <td class="nowrap">{{ duration(s.DurationMs) }}</td>
                                        <td class="nowrap">{{ s.CreatedAt | date:'MMM d, h:mm:ss a' }}</td>
                                        <td class="wrap bad">{{ s.Error || '' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </mat-tab>

                <!-- ===================== Dead letters ===================== -->
                <mat-tab *ngIf="t.DeadLetters.length">
                    <ng-template mat-tab-label>
                        Dead letters
                        <span class="tab-count has-errors">{{ t.DeadLetters.length }}</span>
                    </ng-template>
                    <div class="tab-pane">
                        <div class="table-scroll">
                            <table class="df-table">
                                <thead>
                                    <tr>
                                        <th scope="col">Id</th>
                                        <th scope="col">Queue</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Reason</th>
                                        <th scope="col">Description</th>
                                        <th scope="col">Dead-lettered</th>
                                        <th scope="col" class="num">Replays</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr *ngFor="let d of t.DeadLetters">
                                        <td class="mono">{{ d.DeadLetterId }}</td>
                                        <td class="mono">{{ d.Queue }}</td>
                                        <td>
                                            <span class="df-badge" [ngClass]="'tone-' + statusMeta(d.Status).tone">
                                                <mat-icon aria-hidden="true">{{ statusMeta(d.Status).icon }}</mat-icon>
                                                {{ d.Status }}
                                            </span>
                                        </td>
                                        <td class="wrap strong">{{ d.Reason || '-' }}</td>
                                        <td class="wrap">{{ d.Description || '-' }}</td>
                                        <td class="nowrap">{{ d.DeadLetteredAt | date:'MMM d, h:mm:ss a' }}</td>
                                        <td class="num">{{ d.ReplayCount }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </mat-tab>
            </mat-tab-group>
        </ng-container>
    </div>
</div>

<ng-template #dash><span class="muted">-</span></ng-template>
